import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
} from '@mui/material';

import { useCreateSupplier } from '~/hooks/apis/tours/useTourSuppliers';

import { Supplier } from '~/types/tours/supplier';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: (supplier: Supplier) => void;
}

interface SupplierForm {
  name: string;
  email: string;
  phone: string;
  address: string;
}

const EMPTY_FORM: SupplierForm = { name: '', email: '', phone: '', address: '' };

const FIELDS: Array<{ key: keyof SupplierForm; label: string }> = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'address', label: 'Address' },
];

/** svc-tour requires every field, so all four must be filled to save. */
export default function SupplierFormDialog({ open, onClose, onCreated }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState<SupplierForm>(EMPTY_FORM);

  const createSupplier = useCreateSupplier();

  const isComplete = FIELDS.every(({ key }) => form[key].trim());

  const handleClose = useCallback(() => {
    setForm(EMPTY_FORM);
    createSupplier.reset();
    onClose();
  }, [createSupplier, onClose]);

  const handleSave = useCallback(async () => {
    try {
      const supplier = await createSupplier.mutateAsync({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
      });

      enqueueSnackbar(`Created supplier ${supplier.name}`, { variant: 'success' });
      onCreated?.(supplier);
      handleClose();
    } catch {
      // The mutation error is shown in the dialog.
    }
  }, [createSupplier, enqueueSnackbar, form, handleClose, onCreated]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Create supplier</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {FIELDS.map(({ key, label }) => (
            <TextField
              key={key}
              label={label}
              value={form[key]}
              onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))}
              fullWidth
            />
          ))}

          {!!createSupplier.error && <Alert severity="error">{createSupplier.error.message}</Alert>}
        </Stack>
      </DialogContent>
      {createSupplier.isPending && <LinearProgress />}
      <DialogActions>
        <Button onClick={handleClose} disabled={createSupplier.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!isComplete || createSupplier.isPending}>
          Create supplier
        </Button>
      </DialogActions>
    </Dialog>
  );
}
